import { useMemo, useRef } from "react";

import type { Node, Edge } from "@xyflow/react";

import {
  type PipelineGroupService,
  type PipelineNodeData,
  type PipelineNodeType,
  aggregateStatus,
  pickActiveService,
  getServiceStatus,
  getLayoutedElements,
} from "./pipeline-graph-helpers";
import type { DeploymentEvent } from "./useDeploymentEvents";

// ── Types (re-exported for backwards compatibility) ───────────────────────────

export type {
  PipelineNodeType,
  PipelineNodeEvent,
  PipelineGroupService,
  PipelineNodeData,
} from "./pipeline-graph-helpers";
export type { PipelineStep } from "./pipeline-graph-from-state-helpers";
export {
  usePipelineGraphFromState,
  type UsePipelineGraphFromStateResult,
} from "./usePipelineGraphFromState";

// ── Hook ───────────────────────────────────────────────────────────────────────

export interface UsePipelineGraphResult {
  nodes: Node[];
  edges: Edge[];
  serviceNames: string[];
}

export function usePipelineGraph(
  events: DeploymentEvent[] = [],
  templateServices?: { name: string; type: string }[],
  deployMode?: string,
  vcsConnected?: boolean,
  /** Override the VCS badge label (e.g. "Connected — Provisioning" when stack is provisioning) */
  _vcsStatusLabel?: string,
  /** Whether the org has a VCS connection (even if the app has no repo yet) */
  _vcsOrgConnected?: boolean,
  /** The app's VCS repo URL — null means repo not provisioned yet */
  _vcsRepoUrl?: string | null,
): UsePipelineGraphResult {
  const prevPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  return useMemo(() => {
    const nodes: Node<PipelineNodeData>[] = [];
    const edges: Edge[] = [];
    const serviceNames: string[] = [];

    // Handle null/undefined events
    const safeEvents = events ?? [];

    // All services from template (including stateful). Stateful services
    // (db, cache, queue) use pre-built images and skip the build step —
    // they get deploy-only nodes.
    const allServices = templateServices ?? [];
    const buildableServices = allServices.filter((s) => s.type !== "stateful");
    const statefulServices = allServices.filter((s) => s.type === "stateful");

    // Deduplicate service names from events (for when templateServices unavailable)
    const eventServiceNames = new Set<string>();
    for (const e of safeEvents) {
      const sn = e.metadata?.serviceName as string | undefined;
      if (sn) eventServiceNames.add(sn);
    }

    // When no template services, derive from events and treat unknowns as buildable
    const effectiveBuildable =
      buildableServices.length > 0
        ? buildableServices
        : Array.from(eventServiceNames).map((name) => ({
            name,
            type: "unknown",
          }));

    // VCS is decoupled from the DAG — shown in the Setup stage sidebar, not as a node.

    // ── Build group node + Deploy group node ──
    if (
      effectiveBuildable.length > 0 ||
      statefulServices.length > 0 ||
      allServices.length > 0
    ) {
      if (deployMode === "ci_managed") {
        // CI-managed: single CI group node
        const ciServices: PipelineGroupService[] = [
          ...effectiveBuildable,
          ...statefulServices,
        ].map((svc) => {
          const st = getServiceStatus(svc.name, "build", events);
          serviceNames.push(svc.name);
          return {
            name: svc.name,
            type: svc.type,
            status: st.status,
            message: st.message,
          };
        });

        const ciAgg = aggregateStatus(ciServices);
        const activeService = pickActiveService(ciServices);
        nodes.push({
          id: "group-ci",
          type: "pipeline-group",
          position: { x: 0, y: 0 },
          data: {
            label: "External CI",
            step: "ci",
            nodeType: "group-ci" as PipelineNodeType,
            status: ciAgg,
            message: "",
            iconName: "Build",
            serviceName: activeService,
            isStale: false,
            retryCount: 0,
            deployModeBadge: "External",
            services: ciServices,
          },
        });

        // CI group node is standalone — no incoming edge
      } else {
        // Non-CI-managed: Build group (server + static) → Deploy group (all)
        const buildBadge =
          deployMode === "ci_build_rusha_deploy" ? "CI" : undefined;
        const deployBadge =
          deployMode === "ci_build_rusha_deploy"
            ? "Rusha"
            : deployMode === "rusha_build_ci_deploy"
              ? "CI"
              : undefined;

        const buildServices: PipelineGroupService[] = effectiveBuildable.map(
          (svc) => {
            const st = getServiceStatus(svc.name, "build", events);
            serviceNames.push(svc.name);
            return {
              name: svc.name,
              type: svc.type,
              status: st.status,
              message: st.message,
            };
          },
        );

        const allDeployServices: PipelineGroupService[] = [
          ...effectiveBuildable,
          ...statefulServices,
        ].map((svc) => {
          const st = getServiceStatus(svc.name, "deploy", events);
          if (!serviceNames.includes(svc.name)) serviceNames.push(svc.name);
          return {
            name: svc.name,
            type: svc.type,
            status: st.status,
            message: st.message,
          };
        });

        if (buildServices.length > 0) {
          const buildAgg = aggregateStatus(buildServices);
          const activeBuildSvc = pickActiveService(buildServices);
          nodes.push({
            id: "group-build",
            type: "pipeline-group",
            position: { x: 0, y: 0 },
            data: {
              label: "Build",
              step: "build",
              nodeType: "group-build" as PipelineNodeType,
              status: buildAgg,
              message: "",
              iconName: "Build",
              serviceName: activeBuildSvc,
              isStale: false,
              retryCount: 0,
              deployModeBadge: buildBadge,
              services: buildServices,
            },
          });

          const _buildEdgeLabel =
            buildAgg === "completed"
              ? "✓"
              : buildAgg === "failed"
                ? "✗"
                : undefined;
          // Build is the first node — no incoming edge
        }

        if (allDeployServices.length > 0) {
          const deployAgg = aggregateStatus(allDeployServices);
          const activeDeploySvc = pickActiveService(allDeployServices);
          nodes.push({
            id: "group-deploy",
            type: "pipeline-group",
            position: { x: 0, y: 0 },
            data: {
              label: "Deploy",
              step: "deploy",
              nodeType: "group-deploy" as PipelineNodeType,
              status: deployAgg,
              message: "",
              iconName: "RocketLaunch",
              serviceName: activeDeploySvc,
              isStale: false,
              retryCount: 0,
              deployModeBadge: deployBadge,
              services: allDeployServices,
            },
          });

          const deployEdgeLabel =
            deployAgg === "completed"
              ? "✓"
              : deployAgg === "failed"
                ? "✗"
                : undefined;
          const deployEdgeSource =
            buildServices.length > 0 ? "group-build" : null;
          if (deployEdgeSource) {
            edges.push({
              id: "edge-build-group-deploy",
              source: deployEdgeSource,
              target: "group-deploy",
              type: "default",
              animated: deployAgg === "in_progress",
              label: deployEdgeLabel,
              labelStyle: {
                fill: deployAgg === "failed" ? "#d32f2f" : "#666",
                fontWeight: 600,
                fontSize: 10,
              },
              labelShowBg: false,
            });
          } // close if (deployEdgeSource)
        }
      }
    }

    // Apply dagre layout
    const layoutedNodes = getLayoutedElements(nodes, edges);

    // Cache positions for smooth transitions on re-layout
    const newPositions = new Map<string, { x: number; y: number }>();
    for (const node of layoutedNodes) {
      if (node.position) newPositions.set(node.id, node.position);
    }
    prevPositionsRef.current = newPositions;

    return { nodes: layoutedNodes, edges, serviceNames };
  }, [events, templateServices, deployMode, vcsConnected]);
}
